import { createAsyncThunk } from '@reduxjs/toolkit';
import { UserRepository } from 'repositories/UserRepository';
import { IAuthState } from 'store/slices/authSlice';
import { EApiResponseStatus, IApiResponseDetails } from 'types/api';
import {
  IReferralPayout,
  IReferralReward,
  IReferralStats,
  IReferralUser,
  TReferralPayoutsListQuery,
  TReferralRewardsListQuery,
  TReferralUsersListQuery,
} from 'types';
import { IUserReferalsState } from 'store/slices/userReferralSlice';

export const fetchReferralStats = createAsyncThunk<
  IReferralStats,
  void,
  { state: { auth: IAuthState } }
>('userReferral/referralStats', async (_, { getState }) => {
  const {
    auth: { token },
  } = getState();
  if (!token) throw Error('no token');

  const referralStatsResponse = await UserRepository.getReferralStats(token);
  if (referralStatsResponse.status === EApiResponseStatus.FAILURE)
    throw Error(referralStatsResponse.error);

  const { data } = referralStatsResponse;
  const { balance, level, link, hashRate, rate, maxHashRate } = data[0];
  const referralStats: IReferralStats = {
    referralCurrentBalance: Number.parseFloat(balance).toFixed(2),
    referralCurrentRate: Number.parseFloat(hashRate).toFixed(),
    referralLevel: level,
    referralMaxRate: maxHashRate,
    referralPercent: (
        Number.parseFloat(rate) * 100
    ).toFixed(2),
    referralLinkAddress: link,
  };

  return referralStats;
});

export const fetchReferralUsersList = createAsyncThunk<
  { content: IReferralUser[]; details: IApiResponseDetails },
  void,
  { state: { auth: IAuthState; userReferrals: IUserReferalsState } }
>('userReferral/referralUserList', async (_, { getState }) => {
  const {
    auth: { token },
    userReferrals: {
      referralUsersList: { currentPage, itemsPerPage, search, sortBy, sortDir, fromDate, toDate },
    },
  } = getState();
  if (!token) throw Error('no token');

  const params: TReferralUsersListQuery = {
    page: String(currentPage),
    size: String(itemsPerPage),
  };
  if (search) params.filter = search;
  if (sortBy) {
    params.sort = sortDir ? `referrals.${sortBy},${sortDir.toLowerCase()}` : sortBy;
  }
  if (fromDate) params.from = fromDate;
  if (toDate) params.to = toDate;

  const referralUsersListResponse = await UserRepository.getReferralUsersList(token, params);

  if (referralUsersListResponse.status === EApiResponseStatus.FAILURE)
    throw Error(referralUsersListResponse.error);

  return referralUsersListResponse.data;
});

export const fetchReferralRewardsList = createAsyncThunk<
  { content: IReferralReward[]; details: IApiResponseDetails },
  void,
  { state: { auth: IAuthState; userReferrals: IUserReferalsState } }
>('userReferral/referralRewardsList', async (_, { getState }) => {
  const {
    auth: { token },
    userReferrals: {
      referralRewardsList: { currentPage, itemsPerPage, search, sortBy, sortDir, fromDate, toDate },
    },
  } = getState();
  if (!token) throw Error('no token');

  const params: TReferralRewardsListQuery = {
    page: String(currentPage),
    size: String(itemsPerPage),
  };
  if (search) params.filter = search;
  if (sortBy) {
    params.sort = sortDir ? `referrals.${sortBy},${sortDir.toLowerCase()}` : sortBy;
  }
  if (fromDate) params.from = fromDate;
  if (toDate) params.to = toDate;

  const referralRewardsListResponse = await UserRepository.getReferralRewards(token, params);

  if (referralRewardsListResponse.status === EApiResponseStatus.FAILURE)
    throw Error(referralRewardsListResponse.error);

  return referralRewardsListResponse.data;
});

export const fetchReferralsPayoutsList = createAsyncThunk<
  { content: IReferralPayout[]; details: IApiResponseDetails },
  void,
  { state: { auth: IAuthState; userReferrals: IUserReferalsState } }
>('userReferral/referralPayoutsList', async (_, { getState }) => {
  const {
    auth: { token },
    userReferrals: {
      referralPayoutsList: { currentPage, itemsPerPage, search, sortBy, sortDir, fromDate, toDate },
    },
  } = getState();
  if (!token) throw Error('no token');

  const params: TReferralPayoutsListQuery = {
    page: String(currentPage),
    size: String(itemsPerPage),
  };
  if (search) params.filter = search;
  if (sortBy) {
    params.sort = sortDir ? `referrals.${sortBy},${sortDir.toLowerCase()}` : sortBy;
  }
  if (fromDate) params.from = fromDate;
  if (toDate) params.to = toDate;

  const referralPayoutsListResponse = await UserRepository.getReferralPayouts(token, params);

  if (referralPayoutsListResponse.status === EApiResponseStatus.FAILURE)
    throw Error(referralPayoutsListResponse.error);

  return referralPayoutsListResponse.data;
});
